import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './profileHealthAPI';

// Thunks
export const fetchHealthProfiles = createAsyncThunk('profileHealth/fetchProfiles', async (params, { rejectWithValue }) => {
  try {
    const res = await api.getHealthProfiles(params);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchHealthStats = createAsyncThunk('profileHealth/fetchStats', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getHealthStats();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchProfileDetail = createAsyncThunk('profileHealth/fetchDetail', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getProfileHealthDetail(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const performBulkAction = createAsyncThunk('profileHealth/bulkAction', async (data, { rejectWithValue }) => {
  try {
    const res = await api.bulkHealthAction(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const triggerEvaluateAll = createAsyncThunk('profileHealth/evaluateAll', async (_, { rejectWithValue }) => {
  try {
    const res = await api.evaluateAllProfiles();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice
const profileHealthSlice = createSlice({
  name: 'profileHealth',
  initialState: {
    profiles: [],
    total: 0,
    stats: null,
    detail: null,
    loading: false,
    statsLoading: false,
    detailLoading: false,
    bulkLoading: false,
    error: null,
  },
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch profiles
      .addCase(fetchHealthProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthProfiles.fulfilled, (state, { payload }) => {
        state.profiles = payload.items || payload.Items || [];
        state.total = payload.total ?? payload.Total ?? 0;
        state.loading = false;
      })
      .addCase(fetchHealthProfiles.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // Fetch stats
      .addCase(fetchHealthStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchHealthStats.fulfilled, (state, { payload }) => {
        state.stats = payload;
        state.statsLoading = false;
      })
      .addCase(fetchHealthStats.rejected, (state) => {
        state.statsLoading = false;
      })
      // Fetch detail
      .addCase(fetchProfileDetail.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchProfileDetail.fulfilled, (state, { payload }) => {
        state.detail = payload;
        state.detailLoading = false;
      })
      .addCase(fetchProfileDetail.rejected, (state) => {
        state.detailLoading = false;
      })
      // Bulk action
      .addCase(performBulkAction.pending, (state) => {
        state.bulkLoading = true;
      })
      .addCase(performBulkAction.fulfilled, (state) => {
        state.bulkLoading = false;
      })
      .addCase(performBulkAction.rejected, (state) => {
        state.bulkLoading = false;
      })
      // Evaluate all
      .addCase(triggerEvaluateAll.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(triggerEvaluateAll.fulfilled, (state) => {
        state.statsLoading = false;
      })
      .addCase(triggerEvaluateAll.rejected, (state) => {
        state.statsLoading = false;
      });
  },
});

export const { clearDetail } = profileHealthSlice.actions;

// Selectors
export const selectHealthProfiles = (state) => state.profileHealth.profiles;
export const selectHealthTotal = (state) => state.profileHealth.total;
export const selectHealthStats = (state) => state.profileHealth.stats;
export const selectHealthDetail = (state) => state.profileHealth.detail;
export const selectHealthLoading = (state) => state.profileHealth.loading;
export const selectHealthStatsLoading = (state) => state.profileHealth.statsLoading;
export const selectHealthDetailLoading = (state) => state.profileHealth.detailLoading;
export const selectHealthBulkLoading = (state) => state.profileHealth.bulkLoading;

export default profileHealthSlice.reducer;
